/**
 * @jsxRuntime classic
 * @jsx jsx
 */

import { css, jsx } from '@compiled/react';
import { useIntl, defineMessages } from 'react-intl';

import Button from '@atlaskit/button/button';
import ModalBody from '@atlaskit/modal-dialog/modal-body';
import ModalDialog from '@atlaskit/modal-dialog/modal-dialog';
import AKModalFooter from '@atlaskit/modal-dialog/modal-footer';
import ModalHeader from '@atlaskit/modal-dialog/modal-header';
import ModalTitle from '@atlaskit/modal-dialog/modal-title';
import ModalTransition from '@atlaskit/modal-dialog/modal-transition';
import { Text } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';

export type ExternalLinkModalProps = {
	extensionTitle: string | undefined;
	onCancel: () => void;
	onConfirm: () => void;
	url: URL;
};

const cssActionItem = css({
	flex: '1 0 auto',
	marginTop: 0,
	marginRight: token('space.050'),
	marginBottom: 0,
	marginLeft: token('space.050'),
});

const messages = defineMessages({
	navigationWarningTitle: {
		id: 'forge-ui.custom-ui.bridge.messages.navigation-warning-title',
		defaultMessage: 'Opening external page on {hostname}',
		description:
			'Title for the modal that warns the user that they are about to open an external page',
	},
	navigationWarningBody: {
		id: 'forge-ui.custom-ui.bridge.messages.navigation-warning-body',
		defaultMessage:
			'is sending you to an external page. Ensure you trust that page before you continue.',
		description:
			'Body for the modal that warns the user that they are about to open an external page',
	},
	navigationWarningBodyNoAppName: {
		id: 'forge-ui.custom-ui.bridge.messages.navigation-warning-body-no-app-name',
		defaultMessage:
			'This app is sending you to an external page. Ensure you trust that page before you continue.',
		description:
			'Body for the modal that warns the user that they are about to open an external page without an extension name',
	},
	navigationWarningAppSecurityLink: {
		id: 'forge-ui.custom-ui.bridge.messages.navigation-warning-app-security-link',
		defaultMessage: 'Learn more about app security',
		description: 'Text for the link that takes the user to the app security page',
	},
	navigationWarningCancelButton: {
		id: 'forge-ui.custom-ui.bridge.messages.navigation-warning-cancel-button',
		defaultMessage: 'Cancel',
		description: 'Text for the button that cancels the modal',
	},
	navigationWarningContinueButton: {
		id: 'forge-ui.custom-ui.bridge.messages.navigation-warning-continue-button',
		defaultMessage: 'Continue',
		description: 'Text for the button that continues the modal',
	},
});

type ModalFooterProps = {
	onCancel: () => void;
	onConfirm: () => void;
};

const ModalFooter = ({ onCancel, onConfirm }: ModalFooterProps): JSX.Element => {
	const intl = useIntl();
	return (
		<AKModalFooter>
			<div
				// eslint-disable-next-line @atlaskit/design-system/consistent-css-prop-usage -- to avoid import of css module from @compiled/react
				css={{
					display: 'flex',
					justifyContent: 'space-between',
					width: '100%',
				}}
			>
				<Button
					css={{ padding: 0, '> span': { margin: 0 } }}
					href="https://go.atlassian.com/forge-help-app-security"
					appearance="subtle-link"
					target="_blank"
					rel="noopener noreferrer"
					// eslint-disable-next-line @atlassian/i18n/no-literal-string-in-jsx
				>
					{intl.formatMessage(messages.navigationWarningAppSecurityLink)}
				</Button>
				<div
					// eslint-disable-next-line @atlaskit/design-system/consistent-css-prop-usage, @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
					css={css({
						display: 'inline-flex',
						marginTop: 0,
						marginRight: token('space.negative.050'),
						marginBottom: 0,
						marginLeft: token('space.negative.050'),
					})}
				>
					<div css={cssActionItem}>
						<Button appearance="subtle" onClick={onCancel}>
							{intl.formatMessage(messages.navigationWarningCancelButton)}
						</Button>
					</div>
					<div css={cssActionItem}>
						<Button appearance="warning" onClick={onConfirm}>
							{intl.formatMessage(messages.navigationWarningContinueButton)}
						</Button>
					</div>
				</div>
			</div>
		</AKModalFooter>
	);
};

export const ExternalLinkModal = ({
	url,
	extensionTitle,
	onConfirm,
	onCancel,
}: ExternalLinkModalProps): JSX.Element => {
	const intl = useIntl();

	return (
		<ModalTransition>
			<ModalDialog onClose={onCancel}>
				<ModalHeader>
					<ModalTitle appearance="warning">
						{intl.formatMessage(messages.navigationWarningTitle, {
							hostname: url.hostname,
						})}
					</ModalTitle>
				</ModalHeader>
				<ModalBody>
					{extensionTitle ? (
						<Text as="p">
							<Text as="strong">{extensionTitle}</Text>{' '}
							{intl.formatMessage(messages.navigationWarningBody)}
						</Text>
					) : (
						<Text as="p">{intl.formatMessage(messages.navigationWarningBodyNoAppName)}</Text>
					)}
					{/* eslint-disable-next-line @atlaskit/design-system/consistent-css-prop-usage -- to avoid import of css module from @compiled/react */}
					<Text as="p" maxLines={1}>
						{url.href}
					</Text>
				</ModalBody>
				<ModalFooter onCancel={onCancel} onConfirm={onConfirm} />
			</ModalDialog>
		</ModalTransition>
	);
};
